<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <!-- Confirmation screen — shown after the company is created. No dashboard redirect. -->
    <div
      v-if="submitted"
      class="card p-4 p-md-5 text-center mx-auto international-confirmation"
    >
      <div class="mb-3">
        <font-awesome-icon
          icon="circle-check"
          class="text-success international-confirmation__icon"
        />
      </div>
      <h2 class="mb-3">{{ $t("signup.international.confirmation_title") }}</h2>
      <p class="lead text-muted mb-0">
        {{ $t("signup.international.confirmation_message") }}
      </p>
    </div>

    <!-- Single-step signup form -->
    <div v-else>
      <CountryPickerComponent />

      <DefaultContactReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        class="mb-4"
      />

      <div v-if="errorMessage" class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>

      <div class="d-flex justify-content-end w-100">
        <button
          class="btn btn-primary btn-lg"
          :disabled="loading"
          @click="handleSubmit"
        >
          <span v-if="loading">
            <span
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("common.loading") }}
          </span>
          <span v-else>{{ $t("signup.international.submit") }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useContactReviewValidation } from "@/composables/useContactReviewValidation";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import { useInternationalSignup } from "@/signup/composables/useInternationalSignup";
import DefaultContactReviewConfigurationComponent from "@/signup/default/DefaultContactReviewConfigurationComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { useVuelidate } from "@vuelidate/core";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();
const { accountRules, companyRules } = useContactReviewValidation(t);

const { loading, createCompany } = useInternationalSignup();
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  company: selectedCompany,
  country: selectedCountry,
} = storeToRefs(signupStore);

const submitted = ref(false);
const errorMessage = ref("");

// Shared with DefaultContactReviewConfigurationComponent's inline errors, so the submit
// gate and the errors the user sees in the contact form can't drift apart.
const v$ = useVuelidate(
  { accountData: accountRules, company: companyRules },
  computed(() => ({
    accountData: accountData.value,
    company: selectedCompany.value || {},
  }))
);

onMounted(() => {
  // Ensure an (empty) company exists to bind the contact form to.
  signupStore.createCompanyIfMissing();
});

const handleSubmit = async () => {
  errorMessage.value = "";
  v$.value.$touch();
  if (v$.value.$error) {
    return;
  }

  const company = selectedCompany.value;
  if (!company) return;
  try {
    await createCompany({
      // Non-null by the time we get here — gated by the validation above.
      account_name: accountData.value.name ?? "",
      phone_number: accountData.value.phone_number ?? "",
      name: company.name,
      registry_code: company.registry_code ?? "",
      address: company.address ?? "",
      vat: company.vat ?? null,
      // The selected country lives on the store (sourced from the URL), not on company.country yet.
      // Persist it into the submitted payload so the backend records it (see spec watch-out).
      country: selectedCountry.value || (company.country ?? ""),
    });
    submitted.value = true;
  } catch (err: any) {
    // A returning lead resubmitting the same company is rejected with 409. Show a descriptive,
    // localized message inline rather than the raw global error banner; defer anything else to
    // the global handler.
    if (err?.response?.status === 409) {
      errorMessage.value = t("signup.international.duplicate_company_error");
    } else {
      globalErrorHandler(err);
    }
  }
};
</script>

<style scoped lang="scss">
.international-confirmation {
  max-width: 600px;
}
.international-confirmation__icon {
  font-size: 3rem;
}
</style>
